import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { servicesData } from '../../data/services';
import Contact from '../Contact';
import { useRef } from 'react';
import SEO from '../SEO';

// Import all service images
import appointmentAiImage from '../../assets/appointmentai.png';
import customAiImage from '../../assets/customai.png';
import ragBotsImage from '../../assets/RAGai.png';
import webAppAiImage from '../../assets/webappai.png';
import processAiImage from '../../assets/processai.png';
import dataScienceAiImage from '../../assets/datascienceai.png';

export default function Services() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Comprehensive schema data for Services page
  const servicesSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "AI Business Solutions & Services | BetterByAI",
      "description": "Explore our range of AI-powered solutions including custom agents, RAG systems, web applications, automation services and data science solutions.",
      "url": "https://betterbyai.com/services",
      "isPartOf": {
        "@type": "WebSite",
        "name": "BetterByAI",
        "url": "https://betterbyai.com"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": servicesData.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Service",
          "name": service.title,
          "url": `https://betterbyai.com/services/${service.slug}`,
          "description": service.description,
          "provider": {
            "@type": "Organization",
            "name": "BetterByAI",
            "url": "https://betterbyai.com"
          }
        }
      }))
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What types of AI services does BetterByAI offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "BetterByAI offers a comprehensive range of AI services including AI appointment setters, custom AI solutions, RAG bots for knowledge management, AI web applications, process automation, and data science solutions - all designed to improve efficiency and generate revenue for your business."
          }
        },
        {
          "@type": "Question",
          "name": "How do I know which AI service is right for my business?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We begin with a thorough consultation to understand your business challenges, goals, and existing processes. Based on this assessment, we recommend the most impactful AI solutions that align with your specific needs, budget, and expected ROI. Our AI business audit can help identify the optimal opportunities for implementation."
          }
        },
        {
          "@type": "Question",
          "name": "Can AI services be customised for my specific industry?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, all our AI services are highly customisable. We tailor solutions to your industry-specific requirements, whether you're in healthcare, finance, manufacturing, retail, or professional services. Our experience across sectors allows us to implement industry best practices while addressing your unique challenges."
          }
        },
        {
          "@type": "Question",
          "name": "What is the implementation process for your AI services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our implementation process follows a structured methodology: initial assessment, solution design, development, testing, deployment, training, and ongoing support. We use an agile approach with regular checkpoints to ensure the solution meets your requirements. Most implementations are completed within 2-12 weeks depending on complexity."
          }
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="AI Services & Solutions | Custom Development & Implementation | BetterByAI"
        description="Transform your business with our AI services: intelligent agents, RAG systems, web applications, process automation & data science solutions. Tailored for maximum ROI."
        canonical="https://betterbyai.com/services"
        schemaMarkup={servicesSchemas}
        speakable={true}
        keywords="AI services, custom AI development, RAG systems, intelligent agents, AI web applications, process automation, data science solutions, AI implementation UK, business AI services"
      />
      <div className="bg-gradient-to-b from-white via-[#FF7066]/5 to-white overflow-hidden">
        {/* Hero Section */}
        <section className="mt-24 relative overflow-hidden">
          {/* Background Elements Removed */}
          
          <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="text-center max-w-3xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
                >
                  Our Services
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0, y:20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-4xl md:text-5xl font-bold text-primary mb-6"
                >
                  AI Solutions for <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">Modern Businesses</span>
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-xl text-secondary mb-8"
                >
                  Explore our range of AI-powered solutions designed to transform your business operations.
                </motion.p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-white">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {servicesData.map((service, index) => (
                  <motion.div
                    key={service.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 group"
                  >
                    <div className="p-2 w-fit rounded-lg bg-[#FF7066]/10 mb-4">
                      {service.icon && <service.icon className="w-8 h-8 text-[#FF7066] group-hover:text-[#FF8A82] transition-colors" />}
                    </div>
                    <h2 className="text-2xl font-semibold mb-3 text-primary group-hover:text-[#FF7066] transition-colors">{service.title}</h2>
                    <p className="text-secondary mb-4">{service.description}</p>
                    <button
                      onClick={() => scrollToSection(service.slug)}
                      className="inline-flex items-center text-[#FF7066] font-medium hover:text-[#FF8A82] hover:underline cursor-pointer transition-colors"
                    >
                      Learn more
                      <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Service Sections */}
        {servicesData.map((service, index) => (
          <ServiceSection 
            key={service.slug}
            service={service}
            isEven={index % 2 === 0}
          />
        ))}
        
        
        {/* Contact Component */}
        <Contact />
      </div>
    </>
  );
}

// Service Section Component
function ServiceSection({ service, isEven }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const x = useTransform(
    scrollYProgress, 
    [0, 0.2, 0.8, 1], 
    [isEven ? -50 : 50, 0, 0, isEven ? -50 : 50]
  );
  
  // Get service-specific image
  const getServiceImage = () => {
    switch(service.slug) {
      case 'ai-appointment-setters':
        return appointmentAiImage;
      case 'custom-ai-solutions':
        return customAiImage;
      case 'rag-bots':
        return ragBotsImage;
      case 'ai-web-applications':
        return webAppAiImage;
      case 'process-automation':
        return processAiImage;
      case 'data-science-solutions':
        return dataScienceAiImage;
      default:
        return service.image;
    }
  };
  
  return (
    <section 
      id={service.slug}
      ref={sectionRef}
      className={`py-24 relative ${isEven ? 'bg-white' : 'bg-gray-50'}`}
    >
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
            {/* Image/Visual */}
            <motion.div 
              className="lg:w-1/2"
              style={{ opacity }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#FF7066]/20 to-secondary/20 rounded-2xl blur-lg"></div>
                <div className="relative aspect-[4/4] bg-white rounded-xl overflow-hidden shadow-xl flex items-center justify-center">
                  {getServiceImage() ? (
                    <img 
                      src={getServiceImage()} 
                      alt={service.title} 
                      className="max-w-[80%] max-h-[80%] object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF7066]/5 to-secondary/5">
                      {service.icon && <service.icon className="w-24 h-24 text-[#FF7066]/30" />}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
            
            {/* Content section */}
            <motion.div 
              className="lg:w-1/2"
              style={{ opacity, x }}
            >
              <div className="p-3 w-fit rounded-lg bg-[#FF7066]/10 mb-6">
                {service.icon && <service.icon className="w-10 h-10 text-[#FF7066]" />}
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-primary">
                {service.title}
              </h2>
              
              {/* Category badge - NEW */}
              {service.category && (
                <div className="mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#FF7066]/10 text-[#FF7066] text-xs font-medium">
                    {service.category}
                  </span>
                </div>
              )}
              
              <p className="text-lg text-secondary mb-8">
                {service.description}
              </p>
              
              {/* Key Features - Kept as is */}
              <div className="space-y-4 mb-8">
                {service.features?.slice(0, 3).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="mt-1 p-1 rounded-full bg-[#FF7066]/10">
                      <svg className="w-4 h-4 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p>{feature}</p>
                  </div>
                )) || (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="mt-1 p-1 rounded-full bg-[#FF7066]/10">
                        <svg className="w-4 h-4 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.707-7.707a1 1 0 00-1.414 1.414L8 12.586l7.293-7.293a1 1 0 00-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p>Enterprise-grade solutions with robust security</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="mt-1 p-1 rounded-full bg-[#FF7066]/10">
                        <svg className="w-4 h-4 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p>Customisable to fit your specific workflows</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="mt-1 p-1 rounded-full bg-[#FF7066]/10">
                        <svg className="w-4 h-4 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p>Ongoing support and implementation assistance</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* UPDATED Benefits Section - Now using compact badges instead of a grid */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Key Benefits</h3>
                <div className="flex flex-wrap gap-2">
                  {service.benefits?.slice(0, 6).map((benefit, idx) => (
                    <span 
                      key={idx} 
                      className="bg-[#FF7066]/5 text-secondary px-3 py-1 rounded-full text-sm font-medium border border-[#FF7066]/10 inline-flex items-center"
                    >
                      <svg className="w-3 h-3 text-[#FF7066] mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"></path>
                      </svg>
                      {benefit}
                    </span>
                  )) || (
                    <>
                      <span className="bg-[#FF7066]/5 text-secondary px-3 py-1 rounded-full text-sm font-medium border border-[#FF7066]/10 inline-flex items-center">
                        <svg className="w-3 h-3 text-[#FF7066] mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586l7.707-7.707a1 1 0 00-1.414-1.414L8 12.586l7.293-7.293a1 1 0 00-1.414 0z"></path>
                        </svg>
                        Increased efficiency
                      </span>
                      <span className="bg-[#FF7066]/5 text-secondary px-3 py-1 rounded-full text-sm font-medium border border-[#FF7066]/10 inline-flex items-center">
                        <svg className="w-3 h-3 text-[#FF7066] mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586l7.707-7.707a1 1 0 00-1.414-1.414L8 12.586l7.293-7.293a1 1 0 00-1.414 0z"></path>
                        </svg>
                        Cost reduction
                      </span>
                      <span className="bg-[#FF7066]/5 text-secondary px-3 py-1 rounded-full text-sm font-medium border border-[#FF7066]/10 inline-flex items-center">
                        <svg className="w-3 h-3 text-[#FF7066] mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586l7.707-7.707a1 1 0 00-1.414-1.414L8 12.586l7.293-7.293a1 1 0 00-1.414 0z"></path>
                        </svg>
                        Better data insights
                      </span>
                      <span className="bg-[#FF7066]/5 text-secondary px-3 py-1 rounded-full text-sm font-medium border border-[#FF7066]/10 inline-flex items-center">
                        <svg className="w-3 h-3 text-[#FF7066] mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586l7.707-7.707a1 1 0 00-1.414-1.414L8 12.586l7.293-7.293a1 1 0 00-1.414 0z"></path>
                        </svg>
                        Enhanced customer satisfaction
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              <Link 
                to={`/services/${service.slug}`}
                className="inline-flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors"
              >
                Explore {service.title} in detail
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}